import { pick } from 'ramda';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import * as oldServer from 'js/hooks/user/server';
import { useUserSelector } from 'js/store/hooks';
import * as actions from 'js/hooks/user/store/actions';
import { parseUserDetails } from 'js/hooks/user/helpers';
import { usePlayerFields } from 'js/hooks/usePlayerFields';
import { useFetchParams, useFetchBalance } from 'js/hooks/user/hooks';
import {
    useChangePassword,
    useGetPlayerTags,
    useSetPlayerTags,
    useUpdatePlayerInfo,
    useValidatePassword
} from 'server';

import * as hooks from './hooks';

const notLoggedIn = () => Promise.reject(new Error('User Public API: User must be logged in.'));

export const setPlayerTagsLegacy = (isLoggedIn, setPlayerTags) => (tags = [], isForce) => {
    if (!isLoggedIn && !isForce) {
        return notLoggedIn();
    }

    return setPlayerTags({ tags });
};

export const getPlayerTagsLegacy = (isLoggedIn, getPlayerTags) => (tagName, isForce) => {
    if (!isLoggedIn && !isForce) {
        return notLoggedIn();
    }

    return getPlayerTags({ tagName })
        .then((data) => ({ data }));
};

const fetchUserDetails = (isLoggedIn, networkTypes, networkDataFilters, defaultPlayerFields) => async (playerFields = []) => {
    if (!isLoggedIn) {
        return notLoggedIn();
    }

    const fields = playerFields?.length ? playerFields : defaultPlayerFields;
    const response = await oldServer.fetchUserDetails(fields, networkTypes, networkDataFilters);

    return { data: parseUserDetails(response.data) };
};

const setCountry = dispatch => country => dispatch(actions.setCountry(country));

export const useUserPublicAPI = () => {
    const dispatch = useDispatch();
    const {
        lang,
        role,
        country,
        isLoggedIn,
        currencyCode,
        countrySetByGeoIp
    } = useUserSelector(pick(['lang', 'role', 'country', 'isLoggedIn', 'currencyCode', 'countrySetByGeoIp']));
    const { networkTypes, networkDataFilters } = useFetchParams();
    const defaultPlayerFields = usePlayerFields();
    const fetchBalance = useFetchBalance();
    const getGeoData = hooks.useGetGeoData();
    const changePassword = useChangePassword();
    const updatePlayerInfo = useUpdatePlayerInfo();
    const validatePassword = useValidatePassword();
    const getPlayerTags = useGetPlayerTags();
    const setPlayerTags = useSetPlayerTags();

    return useMemo(
        () => ({
            /**
             * Gets player basic data.
             * @memberOf Playtech.API.user
             * @function
             * @returns {{ lang: string, country: string, currencyCode: string, countrySetByGeoIp: string }}
             * @example
             * Playtech.API.user.getPlayerBasicData();
             */
            getPlayerBasicData: () => ({
                lang,
                role,
                country,
                currencyCode,
                countrySetByGeoIp
            }),
            /**
             * Retrieve all player tags or a specific one. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/RegistrationService/operations/GetPlayerTags.html}.
             * @memberOf Playtech.API.user
             * @function
             * @param {string} [tagName] If set, retrieve only this tag.
             * @param {boolean} [isForce] If set, try to retrieve tag before login.
             * @returns {Promise<{data: {tags: {name: string, type: string, value: string, expirationDate: Array<string>}}>}
             * @example
             * Playtech.API.user.getPlayerTags('Playertag', true);
             */
            getPlayerTags: getPlayerTagsLegacy(isLoggedIn, getPlayerTags),
            /**
             * Set tags for a current player.
             * @memberOf Playtech.API.user
             * @function
             * @param {tags[]} [tags=[]] List of tags.
             * @param {string} [tags[].name]]
             * @param {string} [tags[].value]
             * @param {string} [tags[].type]
             * @param {string} [tags[].expirationDate]
             * @param {boolean} [isForce] If set, try to set tag before login.
             * @returns {Promise<{ data: { tags: Array<{name: string, type: string, value: string, expirationDate: string}>}}>}
             * @example
             * Playtech.API.user.setPlayerTags(
             *      [{
             *         name: 'tag1',
             *         value: 'value1',
             *         type: 'text',
             *         expirationDate: '2024-04-13'
             *      }],
             *      true
             * );
             */
            setPlayerTags: setPlayerTagsLegacy(isLoggedIn, setPlayerTags),
            /**
             * Get player data. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/RegistrationService/operations/GetPlayerInfo.html}.
             * @memberOf Playtech.API.user
             * @function
             * @param {number[]} [playerFields=[defaults...]]
             * @returns {Promise<{data: Object}>}
             * @example
             * Playtech.API.user.getPlayerData([1]);
             */
            getPlayerData: fetchUserDetails(isLoggedIn, networkTypes, networkDataFilters, defaultPlayerFields),
            /**
             * Set country.
             * @memberOf Playtech.API.user
             * @function
             * @param {string} country
             * @example
             * Playtech.API.user.setCountry('UA');
             */
            setCountry: setCountry(dispatch),
            /**
             * Retrieve player balances based on "Subscription balance types" and updates user's balance in store. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/WalletService/operations/GetDynamicBalances.html}.
             * @memberOf Playtech.API.user
             * @function
             * @returns {Promise<{Object}>} {data: {balances: {balanceType: string, balance: {amount: string, currencyCode: string}}[]}}
             * @example
             * Playtech.API.user.fetchBalance().then((data) => { ... });
             */
            fetchBalance,
            /**
             * Return whether the user is logged.
             * @memberOf Playtech.API.user
             * @function
             * @returns {boolean}
             * @example
             * Playtech.API.user.isLoggedIn();
             */
            isLoggedIn: () => isLoggedIn,
            /**
             * Retrieve player's geo data.
             * @memberOf Playtech.API.user
             * @function
             * @returns {Promise<{country: string, state: string, city: string, latitude: string, longitude: string, accuracyRadius: string}>}
             * @example
             * Playtech.API.user.getGeoData();
             */
            getGeoData,
            /**
             * Validate player's password. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/UserService/operations/ValidatePassword.html}.
             * @memberOf Playtech.API.user
             * @function
             * @param {Object} options
             * @param {string} options.password
             * @returns {Promise<void>}
             * @example
             * Playtech.API.user.validatePassword({ password }).catch(({ response: { data: { errorMessage, errorCode } }, request, reason }) => { ... });
             * @example
             * Playtech.API.user.validatePassword({ password }).then(({ response: {data: { correlationId } } }) => { ... });
             */
            validatePassword,
            /**
             * Change player's password. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/AuthenticationService/operations/ChangePassword.html}.
             * @memberOf Playtech.API.user
             * @param {Object} options
             * @param {string} options.newPassword
             * @param {string} options.oldPassword
             * @function
             * @returns {Promise<void>}
             * @example
             * Playtech.API.user.changePassword({ newPassword, oldPassword });
             */
            changePassword,
            /**
             * Update player's information. See [ims docs]{@link https://integration.playtech.com/apis/openapi/services/RegistrationService/operations/UpdatePlayerInfo.html}.
             * @memberOf Playtech.API.user
             * @param {Object} options
             * @param {Object} options.dataMap
             * @param {Object} options.addressDetails
             * @param {Object} options.contactAddress
             * @param {Object[]} options.customData
             * @param {string} options.customData[].key
             * @param {string} options.customData[].value
             * @param {string} options.changeTimestamp
             * @function
             * @returns {Promise<{ playerCode: number, actions: Object }>}
             * @example
             * Playtech.API.user.updatePlayerInfo({ dataMap, addressDetails, contactAddress, customData, changeTimestamp });
             */
            updatePlayerInfo
        }),
        [
            isLoggedIn,
            getPlayerTags,
            setPlayerTags,
            networkTypes,
            networkDataFilters,
            defaultPlayerFields,
            dispatch,
            fetchBalance,
            getGeoData,
            validatePassword,
            changePassword,
            updatePlayerInfo,
            lang,
            role,
            country,
            currencyCode,
            countrySetByGeoIp
        ]
    );
};
